// Per-route YouTube video configuration. Each entry powers both the on-page
// <VideoEmbed> iframe and the VideoObject JSON-LD injected via head().
// Use a real video ID per slug for full Search Console Video Indexing coverage.
// Until then, a single placeholder video keeps the schema valid and gives
// Google a concrete videoObject to crawl.

export type VideoConfig = {
  videoId: string;
  name: string;
  description: string;
  uploadDate: string; // ISO 8601
  thumbnailUrl?: string; // overrides auto-generated YT thumbnail
};

export const DEFAULT_UPLOAD_DATE = "2026-01-15T09:00:00+05:00";

function ytThumb(videoId: string): string {
  return `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`;
}

// Hardcoded placeholder embeds were removed (the previous default
// pointed to a discovered Rickroll video — `dQw4w9WgXcQ` — that leaked
// into Search Console video indexing). Real videos are now sourced
// exclusively from the admin-managed DB override layer (see
// site-context.functions.ts). Adding entries back here would re-pollute
// crawl output, so keep these maps empty by design.
export const UTILITY_VIDEOS: Record<string, VideoConfig> = {};
export const GOLD_CITY_VIDEOS: Record<string, VideoConfig> = {};

export function getUtilityVideo(slug: string): VideoConfig | undefined {
  return UTILITY_VIDEOS[slug];
}

export function getGoldCityVideo(slug: string): VideoConfig | undefined {
  return GOLD_CITY_VIDEOS[slug];
}

export function buildVideoObjectLd(args: {
  video: VideoConfig;
  pageUrl: string;
}): { type: "application/ld+json"; children: string } {
  const { video, pageUrl } = args;
  const thumb = video.thumbnailUrl ?? ytThumb(video.videoId);
  const embedUrl = `https://www.youtube.com/embed/${video.videoId}`;
  return {
    type: "application/ld+json",
    children: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "VideoObject",
      name: video.name,
      description: video.description,
      thumbnailUrl: [thumb],
      uploadDate: video.uploadDate,
      embedUrl,
      contentUrl: `https://www.youtube.com/watch?v=${video.videoId}`,
      potentialAction: {
        "@type": "SeekToAction",
        target: `${pageUrl}?t={seek_to_second_number}`,
        "startOffset-input": "required name=seek_to_second_number",
      },
    }),
  };
}
